import {
  PlacedBlock,
  SocketConnection,
  ConnectionMarker,
  IsoPoint,
} from '@/app/types/cad';
import { getTransformedSockets, getTransformedLineEndpoints } from './transform';

/**
 * Create connection markers for a socket connection
 * Returns TWO markers - one at source, one at target
 */
export function createConnectionMarker(
  connection: SocketConnection,
  blocks: PlacedBlock[]
): ConnectionMarker[] {
  const sourceBlock = blocks.find((b) => b.id === connection.sourceBlockId);
  const targetBlock = blocks.find((b) => b.id === connection.targetBlockId);

  if (!sourceBlock || !targetBlock) {
    return [];
  }

  const markers: ConnectionMarker[] = [];

  // Get source position
  let sourcePos: IsoPoint | null = null;
  if (connection.sourceSocketIndex !== undefined) {
    const sourceSockets = getTransformedSockets(
      sourceBlock.blockData.elements,
      sourceBlock.transform
    );
    const sourceSocket = sourceSockets.find(
      (s) => s.index === connection.sourceSocketIndex
    );
    if (sourceSocket) sourcePos = sourceSocket.position;
  } else if (connection.sourceLineIndex !== undefined) {
    const sourceLines = getTransformedLineEndpoints(
      sourceBlock.blockData.elements,
      sourceBlock.transform
    );
    const sourceLine = sourceLines.find(
      (l) => l.index === connection.sourceLineIndex
    );
    if (sourceLine) {
      sourcePos = connection.sourceIsStart
        ? sourceLine.startPosition
        : sourceLine.endPosition;
    }
  }

  // Get target position
  let targetPos: IsoPoint | null = null;
  if (connection.targetSocketIndex !== undefined) {
    const targetSockets = getTransformedSockets(
      targetBlock.blockData.elements,
      targetBlock.transform
    );
    const targetSocket = targetSockets.find(
      (s) => s.index === connection.targetSocketIndex
    );
    if (targetSocket) targetPos = targetSocket.position;
  } else if (connection.targetLineIndex !== undefined) {
    const targetLines = getTransformedLineEndpoints(
      targetBlock.blockData.elements,
      targetBlock.transform
    );
    const targetLine = targetLines.find(
      (l) => l.index === connection.targetLineIndex
    );
    if (targetLine) {
      targetPos = connection.targetIsStart
        ? targetLine.startPosition
        : targetLine.endPosition;
    }
  }

  if (!sourcePos || !targetPos) {
    return [];
  }

  // Get system color from source block
  const systemColor = sourceBlock.systemId ? '#3b82f6' : '#3b82f6'; // Will be updated by renderer

  // Create marker at source position
  markers.push({
    id: `marker-${connection.id}-source`,
    type: 'connection-marker',
    position: sourcePos,
    connectionId: connection.id,
    radius: 6,
    color: systemColor,
    fillColor: systemColor,
    filled: true,
  });

  // Create marker at target position
  markers.push({
    id: `marker-${connection.id}-target`,
    type: 'connection-marker',
    position: targetPos,
    connectionId: connection.id,
    radius: 6,
    color: systemColor,
    fillColor: systemColor,
    filled: true,
  });

  return markers;
}

/**
 * Update all connection markers based on current connections and blocks
 */
export function updateConnectionMarkers(
  connections: SocketConnection[],
  blocks: PlacedBlock[],
  existingMarkers: ConnectionMarker[]
): ConnectionMarker[] {
  const newMarkers: ConnectionMarker[] = [];

  // Create markers for all valid connections
  for (const connection of connections) {
    const markers = createConnectionMarker(connection, blocks);
    
    // Preserve existing marker IDs if they exist
    for (const marker of markers) {
      const existingMarker = existingMarkers.find(
        (m) => m.id === marker.id
      );
      if (existingMarker) {
        // Keep the existing ID
        marker.id = existingMarker.id;
      }
      newMarkers.push(marker);
    }
  }

  return newMarkers;
}

/**
 * Check if a connection is still valid
 */
export function isConnectionValid(
  connection: SocketConnection,
  blocks: PlacedBlock[]
): boolean {
  // Prüfe ob Connection eine Position hat (neues Format)
  if (!connection.position || connection.position.u === undefined || connection.position.v === undefined) {
    console.log('❌ Connection ohne Position ist ungültig:', connection.id);
    return false;
  }
  
  // Neues Format: prüfe ob alle verbundenen Sockets noch existieren
  if (connection.connectedSockets && connection.connectedSockets.length > 0) {
    // Prüfe ob alle Blöcke existieren
    const allBlocksExist = connection.connectedSockets.every(socket => 
      blocks.some(b => b.id === socket.blockId)
    );
    
    if (!allBlocksExist) {
      console.log('❌ Connection hat nicht-existierende Blöcke:', connection.id);
      return false;
    }
    
    // Wenn connectedSockets vorhanden ist, ist es eine neue Connection
    return true;
  }
  
  // Legacy Format: prüfe sourceBlockId/targetBlockId
  const sourceBlock = blocks.find((b) => b.id === connection.sourceBlockId);
  const targetBlock = blocks.find((b) => b.id === connection.targetBlockId);

  if (!sourceBlock || !targetBlock) {
    console.log('❌ Legacy connection hat fehlende Blöcke:', connection.id);
    return false;
  }

  // Check if source endpoint still exists
  let sourceValid = false;
  if (connection.sourceSocketIndex !== undefined) {
    const sourceSockets = getTransformedSockets(
      sourceBlock.blockData.elements,
      sourceBlock.transform
    );
    sourceValid = sourceSockets.some((s) => s.index === connection.sourceSocketIndex);
  } else if (connection.sourceLineIndex !== undefined) {
    const sourceLines = getTransformedLineEndpoints(
      sourceBlock.blockData.elements,
      sourceBlock.transform
    );
    sourceValid = sourceLines.some((l) => l.index === connection.sourceLineIndex);
  }

  // Check if target endpoint still exists
  let targetValid = false;
  if (connection.targetSocketIndex !== undefined) {
    const targetSockets = getTransformedSockets(
      targetBlock.blockData.elements,
      targetBlock.transform
    );
    targetValid = targetSockets.some((s) => s.index === connection.targetSocketIndex);
  } else if (connection.targetLineIndex !== undefined) {
    const targetLines = getTransformedLineEndpoints(
      targetBlock.blockData.elements,
      targetBlock.transform
    );
    targetValid = targetLines.some((l) => l.index === connection.targetLineIndex);
  }

  return sourceValid && targetValid;
}

/**
 * Remove invalid connections and update markers
 */
export function cleanupConnections(
  connections: SocketConnection[],
  blocks: PlacedBlock[]
): {
  validConnections: SocketConnection[];
  removedConnectionIds: string[];
} {
  const validConnections: SocketConnection[] = [];
  const removedConnectionIds: string[] = [];

  for (const connection of connections) {
    if (isConnectionValid(connection, blocks)) {
      validConnections.push(connection);
    } else {
      removedConnectionIds.push(connection.id);
    }
  }

  return { validConnections, removedConnectionIds };
}